import {
    ADDITIONAL_LEGEND_HEIGHT,
    ANNOTATION_AVG,
    ANNOTATION_DASH_STYLE,
    ANNOTATION_FLAG_TITLE_MAP,
    ANNOTATION_LINE_LABEL_INFO,
    ANNOTATION_MAX,
    ANNOTATION_MIN,
    BASE_LEGEND_HEIGHT,
    CHART_FORMAT_CURRENCY_GROWTH,
    ESTIMATE_DASH_STYLE,
    LINE_WEIGHT_MAPPING,
    MULTI_MONTH_THRESHOLD,
    MULTI_YEAR_THRESHOLD,
    SCALE_TYPE_LINEAR,
    SCALE_TYPE_LOG,
    SERIES_TYPE_FLAGS,
    TIME_UNIT_SIZE,
    TOOLTIP_STYLE_BAR,
    TOOLTIP_STYLE_BOX,
    Y_AXIS_CURRENCY_TITLE_OPTIONS,
} from './constants';
import { MUTUAL_FUND_ID_PREFIX, RECESSION_CYCLES } from 'Angular/constants/core.constants';
import { Language } from 'Angular/enums/language.enum';
import { ValueFlagOption } from 'ChartApp/ts/enums/fund-chart-common.enum';
import {
    ChartArrowInfo,
    ChartInfo,
    FundChartConfigurableOptions,
    FundChartDataSeries,
    FundChartDimensions,
} from 'ChartApp/ts/interfaces/fund-chart-common.interface';

import { isSameDateUTC } from 'Angular/utils/date';
import { getFormatter } from 'Angular/utils/number_formatter';

import Highcharts from 'ChartApp/ts/features/fundamental-chart/highcharts';

export abstract class BaseYchartFund {
    highchartOptions: any;

    config: FundChartConfigurableOptions;

    chartData!: any[][];

    chartInfo!: ChartInfo;

    chartDimensions!: FundChartDimensions;

    subchartOffsets!: number[];

    range!: { min: number; max: number };

    annotationLineArrowsMap!: { [a: number]: { [b: number]: ChartArrowInfo } };

    get locale(): Language {
        return (this.chartInfo.language as Language) || Language.ENGLISH;
    }

    constructor(highchartOptions: Highcharts.Options, config: FundChartConfigurableOptions) {
        // We take a copy of the objects because they may be altered and we
        // don't want code that instantiates this class to have the objects
        // it sends in altered out from under it.
        this.highchartOptions = { ...highchartOptions } as any;
        this.config = { ...config } as any;
    }

    /* Override methods */
    abstract calculateSubchartOffsets(): number[];

    abstract drawChartArrowLabel(chart: any, chartArrow: ChartArrowInfo, yAxisIdx: number): void;

    abstract formatAnnotationEventLabel(eventItem: any): void;

    abstract getAnnotationLineLabelY(annotationLineType: string): number | null;

    abstract getChartSizeMultiplier(chartDimensions: FundChartDimensions | null): number;

    abstract getLineWidthFromLineWeight(
        lineWeight: keyof typeof LINE_WEIGHT_MAPPING,
        chartSizeMultiplier: number
    ): number;

    abstract resetDataTooltip(): void;

    abstract setChartHeight(chartDimensions: FundChartDimensions): void;

    abstract setupHoverEvents(): void;

    /* Public static methods */
    static getChartSeriesCurrencyCode(chartDataFormat: string, chartSeriesCurrencyCode: string): string {
        // If the chart is in 'currency_growth' format then pass the currency code for the series.
        let currencyCode = null;
        if (chartDataFormat === CHART_FORMAT_CURRENCY_GROWTH && !!chartSeriesCurrencyCode) {
            currencyCode = chartSeriesCurrencyCode;
        }
        return currencyCode as string;
    }

    /**
     * Gets the currency code used for titling y-axis series, or null when the series isn't money-denominated or carries
     *  no currency
     */
    static getYAxisCurrencyTitle(dataSeries: { currency_code?: string | null; type?: string | null }): string | null {
        const isMoneySeries = !!dataSeries.type && dataSeries.type.includes('money');
        return isMoneySeries && dataSeries.currency_code ? dataSeries.currency_code : null;
    }

    static seriesHasData(series: object): boolean {
        const rawData = (series as { raw_data?: unknown[] }).raw_data;
        return (rawData?.length ?? 0) > 0;
    }

    static panelHasData(panelSeries: object[]): boolean {
        return panelSeries.some((series) => BaseYchartFund.seriesHasData(series));
    }

    /**
     * Whether a y-axis of a panel has nothing to draw, i.e. none of the panel's series on it has data
     */
    static isEmptyYAxis(panelSeries: object[], axisType: string): boolean {
        const axisSeries = panelSeries.filter((series) => (series as { axis_type?: string }).axis_type === axisType);
        return !BaseYchartFund.panelHasData(axisSeries);
    }

    static getYAxisCurrencyTitleOptions(yAxisOptions: any, text: string): any {
        const labels = yAxisOptions.labels || {};
        const fontSize = parseFloat((labels.style || {}).fontSize) || 11;
        return {
            ...Y_AXIS_CURRENCY_TITLE_OPTIONS,
            text,
            x: labels.x ?? 0,
            y: Math.round(fontSize * 1.3),
            style: { ...(labels.style || {}) },
        };
    }

    static getChartSeriesId(dataSeries: { object_calc: string; object_id: string }): string {
        return `${dataSeries.object_calc}_${dataSeries.object_id || ''}`;
    }

    /* Public interface methods */
    setProcessedChartOptions(
        chartInfo: ChartInfo,
        chartElement: any,
        chartDimensions: FundChartDimensions,
        chartId: string | null = null
    ): Highcharts.Options {
        if (chartId !== null) this.highchartOptions.chart.renderTo = chartId;
        this.chartInfo = chartInfo;
        this.chartData = chartInfo.chart_data;
        this.chartDimensions = chartDimensions;
        this.config = this.setDefaults();
        this.setChartHeight(chartDimensions);
        this.subchartOffsets = this.calculateSubchartOffsets();

        this.setupDataSeries();
        this.setupHoverEvents();
        this.setupYAxes();
        this.setupAnnotations();
        this.setupEventFlagAnnotations();
        this.setClickability(chartInfo);

        if (this.config.recessions) {
            this.showRecessions();
        }

        if (this.config.showLatestValueLabels) {
            this.setLatestValueArrowLabels();
        }

        let range = this.getChartDateRange(false);
        if (this.isSingleDayIntradayChart()) {
            // On single day intraday charts, we want to ensure the min and max range are from the same date
            range = this.getChartDateRange(false, new Date(range.max));
            this.setSingleDayIntradayTicks(range);
            this.setSingleDayIntradayBreaks();
        } else if (this.isMultiDayIntradayChart()) {
            this.setMultiDayIntradayTicks(range);
            this.setMultiDayIntradayBreaks();
        } else {
            if (this.isMultiYearChart()) {
                this.highchartOptions.xAxis.labels.format = '{value:%Y}';
                this.highchartOptions.xAxis.minTickInterval = TIME_UNIT_SIZE.year;
            } else if (this.isMultiMonthChart()) {
                this.highchartOptions.xAxis.minTickInterval = TIME_UNIT_SIZE.month;
            }

            const { locale } = this;
            this.highchartOptions.xAxis.labels.formatter = function () {
                const date = new Date(this.value as number);
                const month = date.toLocaleDateString(locale, { month: 'short', timeZone: 'UTC' });
                const format = this.dateTimeLabelFormat as string;
                let suffix = '';
                if (format.includes('e')) {
                    const day = date.toLocaleDateString(locale, { day: '2-digit', timeZone: 'UTC' });
                    suffix = day;
                } else if (format.includes('y')) {
                    const year = date.toLocaleDateString(locale, { year: '2-digit', timeZone: 'UTC' });
                    suffix = `'${year}`;
                } else {
                    return this.axis.defaultLabelFormatter.call(this);
                }
                return `${month} ${suffix}`;
            };
            this.highchartOptions.xAxis.min = range.min;
            this.highchartOptions.xAxis.max = range.max;
        }

        this.setAreaChartThreshold();

        if (this.hasOnlySingleFrequencyData('quarterly') && this.config.quarterlyFormat !== 'month') {
            this.setQuarterlyDateTicks();
        }

        this.range = range;
        this.resetDataTooltip();

        return this.highchartOptions;
    }

    /* Internal methods */

    /**
     * Given a config object, this method cleans all the object properties and set up sane defaults
     * where needed. Note: some of the properties set here are actually used by child classes of BaseYChartFund.
     * We just put them here, so all the config prep happens in one place
     *
     * @param {object} Config object
     * @returns {object} Cleaned config object
     * @memberof BaseYchartFund
     */
    setDefaults(): FundChartConfigurableOptions {
        const config = { ...this.config } as any;

        if (config.adjustLegend === undefined || typeof config.adjustLegend !== 'boolean') {
            config.adjustLegend = false;
        }

        if (config.annualizedReturns === undefined || typeof config.annualizedReturns !== 'boolean') {
            config.annualizedReturns = false;
        }

        if (config.clickable === undefined || typeof config.clickable !== 'boolean') {
            config.clickable = false;
        }

        if (config.dynamicHeight === undefined || typeof config.dynamicHeight !== 'boolean') {
            config.dynamicHeight = false;
        }

        if (config.plotBandsColor === undefined) {
            config.plotBandsColor = '#E2E2E2';
        }

        if (this.chartInfo.chart_quarterly_date_format) {
            config.quarterlyFormat = this.chartInfo.chart_quarterly_date_format;
        }
        if (config.quarterlyFormat === undefined || ['quarter', 'month'].indexOf(config.quarterlyFormat) === -1) {
            config.quarterlyFormat = 'quarter';
        }

        if (config.recessions === undefined || typeof config.recessions !== 'boolean') {
            config.recessions = false;
        }

        if (config.scaleType === undefined || [SCALE_TYPE_LINEAR, SCALE_TYPE_LOG].indexOf(config.scaleType) === -1) {
            config.scaleType = SCALE_TYPE_LINEAR;
        }

        if (config.showLatestValueLabels === undefined || typeof config.showLatestValueLabels !== 'boolean') {
            config.showLatestValueLabels = false;
        }

        if (config.tooltipCallback === undefined || typeof config.tooltipCallback !== 'function') {
            config.tooltipCallback = undefined;
        }

        if (
            config.tooltipStyle === undefined ||
            [TOOLTIP_STYLE_BAR, TOOLTIP_STYLE_BOX].indexOf(config.tooltipStyle) === -1
        ) {
            config.tooltipStyle = TOOLTIP_STYLE_BOX;
        }

        if (config.yAxisWidth === undefined || (config.yAxisWidth !== null && typeof config.yAxisWidth !== 'number')) {
            config.yAxisWidth = 80;
        }

        if (typeof config.showAxisCurrency !== 'boolean') {
            config.showAxisCurrency = false;
        }

        return config;
    }

    /* eslint-disable prefer-destructuring */
    setupDataSeries(): void {
        const series = [];
        const baseSeriesTemplate = {
            data: [],
            dataGrouping: { enabled: false },
            tooltip: {},
            marker: { enabled: false },
        };
        let firstDataIndex;
        let lastDataIndex;

        for (let i = 0; i < this.chartData.length; i += 1) {
            for (let j = 0; j < this.chartData[i].length; j += 1) {
                const chartData = this.chartData[i][j];
                const { color, fill_opacity, weight, hidden, area_chart, arearange_chart, style } =
                    chartData.line_format; /* eslint-disable-line camelcase */

                let dataSeries: FundChartDataSeries;

                // If chart specific series options were provided, merge those with our base series options.
                // Otherwise, just make a copy of the base series options.
                if (this.highchartOptions.series && this.highchartOptions.series[j]) {
                    dataSeries = {
                        ...(baseSeriesTemplate as any),
                        ...(this.highchartOptions.series[j] as any),
                    } as FundChartDataSeries;
                } else {
                    dataSeries = { ...(baseSeriesTemplate as any) } as FundChartDataSeries;
                }
                dataSeries.name = chartData.short_label;
                dataSeries.id = BaseYchartFund.getChartSeriesId(chartData);
                dataSeries.data = chartData.raw_data;
                dataSeries.color = color;
                dataSeries.dashStyle = style;
                dataSeries.zIndex = chartData.z_index; /* eslint-disable-line camelcase */

                const chartSizeMultiplier = this.getChartSizeMultiplier(null);
                dataSeries.lineWidth = this.getLineWidthFromLineWeight(weight, chartSizeMultiplier);

                if (hidden) {
                    dataSeries.visible = false;
                }

                if (area_chart) {
                    /* eslint-disable-line camelcase */
                    dataSeries.type = 'area';
                } else if (arearange_chart) {
                    /* eslint-disable-line camelcase */
                    dataSeries.type = 'arearange';
                    dataSeries.fillOpacity = fill_opacity; /* eslint-disable-line camelcase */
                }

                // If we are returned a 'estimates_start_date', use it to set up estimate zones
                if (chartData.estimates_start_date) {
                    dataSeries.zoneAxis = 'x';
                    dataSeries.zones = [{ value: chartData.estimates_start_date }, { dashStyle: ESTIMATE_DASH_STYLE }];
                }

                if (this.config.tooltipStyle === TOOLTIP_STYLE_BAR) {
                    // Ensure that non-flag tooltips have no format options and do not display.
                    // enabled: false does not work, and you cannot disable mouseTracking
                    dataSeries.tooltip = {
                        footerFormat: '',
                        headerFormat: '',
                        pointFormat: '',
                    };
                }

                series.push(dataSeries);

                if (chartData.raw_data.length > 0) {
                    if (firstDataIndex === undefined || firstDataIndex > chartData.raw_data[0][0]) {
                        firstDataIndex = chartData.raw_data[0][0];
                    }
                    if (
                        lastDataIndex === undefined ||
                        lastDataIndex < chartData.raw_data[chartData.raw_data.length - 1][0]
                    ) {
                        lastDataIndex = chartData.raw_data[chartData.raw_data.length - 1][0];
                    }
                }
            }
        }
        this.highchartOptions.series = series;
        this.chartInfo = {
            ...this.chartInfo,
            firstDataIndex,
            lastDataIndex,
        } as any;
    }

    setupYAxes(): void {
        if (this.config.scaleType === SCALE_TYPE_LOG) {
            this.highchartOptions.yAxis.type = 'logarithmic';
            this.highchartOptions.yAxis.tickPositioner = function () {
                return this.tickPositions;
            };
        } else if (this.config.scaleType === SCALE_TYPE_LINEAR) {
            this.highchartOptions.yAxis.type = 'linear';
        }

        let highchartSeriesIdx = 0; // highcharts has series data in a flat array so we need to map from 2D to 1D
        let highchartYAxisIdx = 0; // y-axis is also a flat array in highcharts

        const yAxes = []; // flat y-axis array to set on highchart

        for (let i = 0; i < this.chartData.length; i += 1) {
            let numYAxesForCurrentChart = 0;
            const currentChartYAxisTypeMap: { [a: string]: number } = {};

            for (let j = 0; j < this.chartData[i].length; j += 1) {
                const dataSeries: FundChartDataSeries = this.chartData[i][j] as FundChartDataSeries;
                const dataSeriesAxisType = dataSeries.axis_type as string;

                if (currentChartYAxisTypeMap[dataSeriesAxisType] !== undefined) {
                    this.highchartOptions.series[highchartSeriesIdx].yAxis =
                        currentChartYAxisTypeMap[dataSeriesAxisType];
                } else {
                    currentChartYAxisTypeMap[dataSeriesAxisType] = highchartYAxisIdx;

                    this.highchartOptions.series[highchartSeriesIdx].yAxis = highchartYAxisIdx;
                    // An axis with nothing to draw is created but hidden, preserving any annotations, unless it would
                    //      be the panel's only visible axis
                    const isEmptyYAxis = BaseYchartFund.isEmptyYAxis(this.chartData[i], dataSeriesAxisType);
                    const isHiddenYAxis =
                        isEmptyYAxis && (BaseYchartFund.panelHasData(this.chartData[i]) || numYAxesForCurrentChart > 0);

                    // Create new settings for this new y-axis
                    const newYAxisOptions = JSON.parse(JSON.stringify(this.highchartOptions.yAxis)) as any;
                    newYAxisOptions.tickPositioner = this.highchartOptions.yAxis.tickPositioner;
                    newYAxisOptions.top = this.subchartOffsets[i];
                    newYAxisOptions.height = this.subchartOffsets[i + 1] - this.subchartOffsets[i];

                    // For smaller tablet / mobile views, adjust the top and the height for the legend to be in
                    // between charts instead of overlayed.
                    if (this.config.adjustLegend) {
                        // Determine number of items in the legend for this axis
                        const itemsInLegend = this.chartData[i].length;
                        const legendHeight = BASE_LEGEND_HEIGHT + (itemsInLegend - 1) * ADDITIONAL_LEGEND_HEIGHT;
                        newYAxisOptions.top += legendHeight;
                        newYAxisOptions.height -= legendHeight;
                    }

                    // Set formatter using current iteration of dataSeries.
                    const currencyCode = BaseYchartFund.getChartSeriesCurrencyCode(
                        dataSeries.format,
                        dataSeries.currency_code
                    ); // eslint-disable-line max-len
                    let labelFormatter = getFormatter(dataSeries.type, currencyCode, this.locale);
                    if (this.config.yAxisFormatter != null) {
                        labelFormatter = this.config.yAxisFormatter;
                    }
                    newYAxisOptions.labels.formatter = function () {
                        return labelFormatter(this.value);
                    };
                    // If this y-axis is the second or more axis on the chart we need to give a few more options
                    // so it displays correctly
                    if (isHiddenYAxis) {
                        newYAxisOptions.visible = false;
                    } else if (numYAxesForCurrentChart > 0) {
                        newYAxisOptions.lineWidth = 1;
                        newYAxisOptions.offset = this.getYAxisWidth() * numYAxesForCurrentChart;
                        if (this.config.scaleType === SCALE_TYPE_LOG) {
                            newYAxisOptions.gridLineWidth = 0;
                        }
                    } else {
                        newYAxisOptions.offset = 0;
                    }
                    // Label a money axis with its currency
                    const currencyTitle =
                        this.config.showAxisCurrency && !isEmptyYAxis
                            ? BaseYchartFund.getYAxisCurrencyTitle(dataSeries)
                            : null;
                    if (currencyTitle) {
                        newYAxisOptions.title = BaseYchartFund.getYAxisCurrencyTitleOptions(
                            newYAxisOptions,
                            currencyTitle
                        );
                    }
                    // Add y-axis to list of y-axes
                    yAxes.push(newYAxisOptions);
                    // Increment number of visible y-axes for the chart since we just added one.
                    if (!isHiddenYAxis) {
                        numYAxesForCurrentChart += 1;
                    }
                    // Increment higcharts y-axis index counter since we just added a y-axis.
                    highchartYAxisIdx += 1;
                }
                // Increment highcharts series index counter.
                highchartSeriesIdx += 1;
            }
        }

        // If we have set a y-axis width, we use that width * the number of y-axes to create room at
        // the margin to show all the axes properly.
        if (this.getYAxisWidth() > 0) {
            // Determined the fixed y-axes width for the chart based on the number of y-axes we have
            this.highchartOptions.chart.marginRight =
                BaseYchartFund.getMaxYAxesForChart(this.chartData) * this.getYAxisWidth();
        }
        this.highchartOptions.yAxis = yAxes;
    }

    setupAnnotations(): void {
        this.annotationLineArrowsMap = {};
        const plotLinesMap: { [a: number]: any[] } = {};
        for (let i = 0; i < this.chartData.length; i += 1) {
            for (let j = 0; j < this.chartData[i].length; j += 1) {
                const chartDataSeries = this.chartData[i][j];
                // Look up series.yAxisIdx by series.id
                const highchartSeries = this.highchartOptions.series.find(
                    (s: { id: string }) => s.id === BaseYchartFund.getChartSeriesId(chartDataSeries)
                ); // eslint-disable-line max-len
                const yAxisIdx = highchartSeries.yAxis as keyof typeof plotLinesMap;
                if (!this.annotationLineArrowsMap[yAxisIdx]) {
                    this.annotationLineArrowsMap[yAxisIdx] = {};
                }
                if (!plotLinesMap[yAxisIdx]) {
                    plotLinesMap[yAxisIdx] = [];
                }
                const annotationData = chartDataSeries.annotation_line_data;
                if (annotationData && chartDataSeries.raw_data.length > 0) {
                    [ANNOTATION_MIN, ANNOTATION_MAX, ANNOTATION_AVG].forEach((annotationLineType: string) => {
                        if (annotationData[annotationLineType as keyof typeof annotationData] !== undefined) {
                            const currencyCode = BaseYchartFund.getChartSeriesCurrencyCode(
                                chartDataSeries.format,
                                chartDataSeries.currency_code
                            ); // eslint-disable-line max-len
                            const labelFormatter = getFormatter(chartDataSeries.type, currencyCode, this.locale); // eslint-disable-line max-len
                            const { color } = highchartSeries;
                            const annotationLine: any = {
                                color,
                                dashStyle: ANNOTATION_DASH_STYLE,
                                value: annotationData[annotationLineType as keyof typeof annotationData].value,
                                width: 2,
                                zIndex: 1,
                            };
                            if (
                                ANNOTATION_LINE_LABEL_INFO[
                                    annotationLineType as keyof typeof ANNOTATION_LINE_LABEL_INFO
                                ].label
                            ) {
                                const xAxisWidth =
                                    this.chartDimensions.width -
                                    this.getYAxisWidth() * BaseYchartFund.getMaxYAxesForChart(this.chartData);
                                let prefix = ANNOTATION_LINE_LABEL_INFO[
                                    annotationLineType as keyof typeof ANNOTATION_LINE_LABEL_INFO
                                ].label?.prefix as string;
                                if (this.locale === Language.FRENCH) {
                                    prefix = ANNOTATION_LINE_LABEL_INFO[
                                        annotationLineType as keyof typeof ANNOTATION_LINE_LABEL_INFO
                                    ].label?.prefix_fr as string;
                                }
                                const formattedValue = labelFormatter(
                                    annotationData[annotationLineType as keyof typeof annotationData].value
                                );
                                const y = this.getAnnotationLineLabelY(annotationLineType);
                                // Determine X position of label relative to left of axis
                                const xPercent =
                                    (annotationData[annotationLineType as keyof typeof annotationData].index -
                                        (this.chartInfo as any).firstDataIndex) /
                                    ((this.chartInfo as any).lastDataIndex - (this.chartInfo as any).firstDataIndex); // eslint-disable-line max-len
                                let x = xPercent * xAxisWidth;
                                // Need to check that we aren't placing the label so that is is cutoff on the x-axis
                                if (x < 5) x += 5;
                                if (x > xAxisWidth - 80) x -= 80;
                                annotationLine.label = {
                                    x,
                                    y,
                                    align: 'left',
                                    text: `${prefix} ${formattedValue}`,
                                    useHTML: true,
                                    style: {
                                        color,
                                        fontSize: this.highchartOptions.xAxis.labels.style.fontSize,
                                        fontWeight: 'normal',
                                        backgroundColor: 'transparent',
                                    },
                                };
                            }
                            plotLinesMap[yAxisIdx].push(annotationLine);
                            if (
                                ANNOTATION_LINE_LABEL_INFO[
                                    annotationLineType as keyof typeof ANNOTATION_LINE_LABEL_INFO
                                ].arrow
                            ) {
                                const suffix = ANNOTATION_LINE_LABEL_INFO[
                                    annotationLineType as keyof typeof ANNOTATION_LINE_LABEL_INFO
                                ].arrow?.suffix as string;
                                const chartArrow = {
                                    color,
                                    suffix,
                                    highchartYAxisIdx: yAxisIdx,
                                    rawYValue: annotationData[annotationLineType].value,
                                    yValue: labelFormatter(annotationData[annotationLineType].value),
                                } as unknown as ChartArrowInfo;
                                this.annotationLineArrowsMap[yAxisIdx][j] = chartArrow;
                            }
                        }
                    });
                }
            }
        }
        const yAxisIndices = Object.keys(plotLinesMap);
        for (let i = 0; i < yAxisIndices.length; i += 1) {
            const index = yAxisIndices[i] as any;
            // Add a plot line at 0 for each y-axis, so it's darker than x/y lines on charts (#E5E5E5)
            plotLinesMap[index].push({
                value: 0,
                color: '#CCCCCC',
                width: 1,
                zIndex: 5,
            });
            this.highchartOptions.yAxis[index].plotLines = plotLinesMap[index];
        }
    }

    static getMaxYAxesForChart(chartData: any[][]): number {
        let maxYAxisTypes = 0;
        for (let i = 0; i < chartData.length; i += 1) {
            // Mirrors setupYAxes: one visible axis per axis type among the series with data, or a single axis for a
            //      panel whose series all have nothing to draw
            const drawableSeries = chartData[i].filter((dataSeries) => BaseYchartFund.seriesHasData(dataSeries));
            const axisTypes = new Set(drawableSeries.map((dataSeries) => dataSeries.axis_type as string));
            const numYAxesForChart = axisTypes.size || (chartData[i].length > 0 ? 1 : 0);
            if (numYAxesForChart > maxYAxisTypes) {
                maxYAxisTypes = numYAxesForChart;
            }
        }
        return maxYAxisTypes;
    }

    setupEventFlagAnnotations(): void {
        const flags = [];
        for (let i = 0; i < this.chartData.length; i += 1) {
            for (let j = 0; j < this.chartData[i].length; j += 1) {
                const dataSeries = this.chartData[i][j];
                if (dataSeries.events_data) {
                    const seriesId = BaseYchartFund.getChartSeriesId(dataSeries);
                    const highchartsSeries = this.highchartOptions.series.find(
                        (s: { id: string }) => s.id === seriesId
                    );
                    const seriesColor = highchartsSeries.color;
                    const yAxis = highchartsSeries.yAxis;
                    const flagSeries = {
                        yAxis,
                        data: [] as any[],
                        fillColor: seriesColor,
                        lineColor: seriesColor,
                        onSeries: seriesId,
                        states: {
                            hover: {
                                fillColor: seriesColor,
                                halo: {
                                    attributes: {
                                        'fill': seriesColor,
                                        'stroke-width': 0,
                                        'translateY': -20,
                                    },
                                    opacity: 0.25,
                                    size: 20,
                                },
                                lineColor: seriesColor,
                                lineWidthPlus: 0,
                            },
                            select: {
                                enabled: false,
                            },
                        },
                        type: SERIES_TYPE_FLAGS,
                    };
                    const fontSize = this.highchartOptions.xAxis.labels.style.fontSize.split('px')[0];
                    for (let k = 0; k < dataSeries.events_data.length; k += 1) {
                        const dataItem = {
                            text: this.formatAnnotationEventLabel(dataSeries.events_data[k]),
                            title: `<div style="position: absolute; left: -${fontSize * 0.75}px; 
                                            top: -${fontSize * 0.2}px; color: white; 
                                    font-size: ${fontSize}px; 
                                    background-color: ${seriesColor}; width: ${fontSize * 1.2}px; height: ${
                                fontSize * 1.2
                            }px; padding: 3px; 
                                    border-radius: 100px; text-align: center; font-family: Roboto Condensed, sans-serif; 
                                    font-weight: bold;">${
                                        ANNOTATION_FLAG_TITLE_MAP[
                                            dataSeries.events_data[k]
                                                .event_subgroup as keyof typeof ANNOTATION_FLAG_TITLE_MAP
                                        ]
                                    }</div>`, // Custom html necessary to align flag title
                            x: dataSeries.events_data[k].event_date_js,
                            y: null, // Set 'y' to null so it does not default to 0, causing issues with axis scale
                        };
                        flagSeries.data.push(dataItem);
                    }
                    flags.push(flagSeries);
                }
            }
        }
        const { series } = this.highchartOptions;
        this.highchartOptions.series = series.concat(flags);
    }

    setLatestValueArrowLabels(): void {
        const { chartData } = this;
        const { lastValueArrowLabelFormatter, lastValueArrowLabelStyles, valueFlags, showLatestValueLabels } =
            this.config;
        const valueFlagsMode = valueFlags ?? (showLatestValueLabels ? ValueFlagOption.VALUE : ValueFlagOption.NONE);
        if (valueFlagsMode === ValueFlagOption.NONE) {
            return;
        }
        const arrowLocale = this.locale;
        const ychartFund = this as any;
        const drawLabel = function (this: any): void {
            let labelIdx = 0;
            // Iterate through every data series provided
            // Use a map to the y-axis it belongs to for determining placement of the arrow
            let yAxisMap = [];
            let highchartSeriesIdx = 0;
            for (let i = 0; i < chartData.length; i += 1) {
                yAxisMap.push([] as ChartArrowInfo[][]);
                for (let j = 0; j < chartData[i].length; j += 1) {
                    // Use the points currently in view, not the full data set. On a drag-select zoom the latest
                    // data point is cropped out of the visible range and Highcharts leaves its plotY at a stale
                    // pre-zoom value, which froze the flag in its old position. The last visible point always has a
                    // current plotY and sits at the right edge where the flag is drawn.
                    const visiblePoints = this.series[highchartSeriesIdx].points;
                    const points =
                        (visiblePoints && visiblePoints.length
                            ? visiblePoints
                            : this.series[highchartSeriesIdx].data) || this.series[highchartSeriesIdx].groupedData; // eslint-disable-line max-len
                    const lastPoint = points[points.length - 1];
                    const seriesVisible = this.series[highchartSeriesIdx].visible;
                    // Check that we have a last point and ensure the last point has a valid `y` attribute.
                    // A null y value could occur if the series's format is changed, for example if the user
                    // tries to use "percent off high" but the last value is negative we cannot calculate it
                    // and thus the last point would have a null `y` value.
                    const chartDataSeries = chartData[i][j];
                    if (
                        lastPoint &&
                        !lastPoint.isNull &&
                        chartDataSeries.hide_last_value_arrow_label !== true &&
                        seriesVisible
                    ) {
                        const currencyCode = BaseYchartFund.getChartSeriesCurrencyCode(
                            chartDataSeries.format,
                            chartDataSeries.currency_code
                        ); // eslint-disable-line max-len
                        let labelFormatter = getFormatter(chartDataSeries.type, currencyCode, arrowLocale); // eslint-disable-line max-len
                        if (lastValueArrowLabelFormatter != null) {
                            labelFormatter = lastValueArrowLabelFormatter;
                        }
                        // highchartOptions.series gets moved to chart.userOptions.series, and highchartOptions.series
                        // gets set to 'null' on initialization of StockChart, so we use chart.userOptions.series.
                        // In inspecting the objects you could use, you might think chart.series would be better to use instead
                        // of chart.userOptions.series, but chart.userOptions.series is a clean copy of the options we passed
                        // in while chart.series contains certain transformations like to the yAxis property that screw
                        // things up.
                        const yAxisIdx = this.userOptions.series[highchartSeriesIdx].yAxis as number;
                        const formattedValue = labelFormatter(lastPoint.y);
                        const ticker = chartDataSeries.object_id?.replace(MUTUAL_FUND_ID_PREFIX, '') ?? '';
                        // Correlation/ratio series have no identifiable ticker (object_id is null -> '' or the
                        // "0" sentinel), so fall back to the formatted value instead of rendering a blank or
                        // meaningless ticker label.
                        const hasTicker = ticker && ticker !== '0';
                        let arrowLabelText = formattedValue;
                        if (hasTicker && valueFlagsMode === ValueFlagOption.TICKER) {
                            arrowLabelText = ticker;
                        } else if (hasTicker && valueFlagsMode === ValueFlagOption.VALUE_AND_TICKER) {
                            // <br> renders the value on a second line below the ticker.
                            arrowLabelText = `${ticker}<br>${formattedValue}`;
                        }
                        const chartArrow = {
                            labelIdx,
                            color: this.userOptions.series[highchartSeriesIdx].color,
                            highchartYAxisIdx: yAxisIdx,
                            plotY: lastPoint.plotY,
                            yValue: arrowLabelText,
                        } as ChartArrowInfo;
                        if (yAxisMap[i][yAxisIdx] !== undefined) {
                            yAxisMap[i][yAxisIdx].push(chartArrow);
                        } else {
                            yAxisMap[i][yAxisIdx] = [chartArrow];
                        }
                        if (
                            points.length > 0 &&
                            ychartFund.annotationLineArrowsMap &&
                            ychartFund.annotationLineArrowsMap[yAxisIdx] &&
                            ychartFund.annotationLineArrowsMap[yAxisIdx][j]
                        ) {
                            const annotationArrow: ChartArrowInfo = ychartFund.annotationLineArrowsMap[yAxisIdx][j];
                            const arrowYValue = parseFloat((annotationArrow as any).rawYValue);
                            // Highcharts provides the ability to calculate the Y-Axis pixel plot point
                            // given the value, so we use that to get the plotY value for annotation labels.
                            const yAxisObject = this.series[highchartSeriesIdx].yAxis;
                            annotationArrow.plotY = yAxisObject.toPixels(arrowYValue, true);

                            if (annotationArrow.labelIdx === undefined) {
                                labelIdx += 1;
                                annotationArrow.labelIdx = labelIdx;
                            }
                            yAxisMap[i][yAxisIdx].push(annotationArrow);
                        }
                    }
                    highchartSeriesIdx += 1;
                    labelIdx += 1;
                }
            }

            // Filter out non-existent y-axes
            yAxisMap = yAxisMap.map((chartYAxes) => {
                return chartYAxes.filter((yAxis) => {
                    return yAxis !== undefined;
                });
            });

            // For each y-axis, sort by values, then place arrows to ensure no overlaps
            const sortFunc = function (a: ChartArrowInfo, b: ChartArrowInfo): number {
                return (a.plotY ?? 0) - (b.plotY ?? 0);
            };
            yAxisMap.forEach((chartYAxes: ChartArrowInfo[][], chartIdx: number) => {
                chartYAxes.forEach((yAxis: ChartArrowInfo[]) =>
                    ychartFund.plotChartArrowLabel(yAxis, chartIdx, sortFunc)
                );
            });

            // Render chart arrows for each chart's y-axes
            // After placement of chart arrows have been determined through Y-values
            // Draw them in the given coordinates
            yAxisMap.forEach((chartYAxes: ChartArrowInfo[][]) => {
                chartYAxes.forEach((yAxis: ChartArrowInfo[], yAxisIdx: number) => {
                    yAxis.forEach((arrow: ChartArrowInfo, arrowIdx: number) => {
                        ychartFund.drawChartArrowLabel(this, arrow, yAxisIdx, arrowIdx, lastValueArrowLabelStyles);
                    });
                });
            });
        };

        this.highchartOptions.chart.events.load = drawLabel;
        // Container resizes call stockChart.setSize() rather than rebuilding the chart, so `load`
        // never fires again and the manually drawn arrow labels would stay at their original pixel
        // positions. Redrawing them on `redraw` repositions the cached SVG elements against the
        // reflowed chart geometry. drawChartArrowLabel guards element creation and always
        // re-translates, so this repositions rather than duplicates.
        this.highchartOptions.chart.events.redraw = drawLabel;
    }

    setClickability(chartInfo: ChartInfo): void {
        if (this.config.clickable) {
            // chartUrl is gotten from data
            const chartUrl = `${chartInfo.chart_url}#/?${chartInfo.chart_url_params}`;

            this.highchartOptions.chart.events.click = function () {
                window.location.href = chartUrl;
            };
        }
    }

    showRecessions(): void {
        const { config } = this;
        const plotBands = [];
        const totalPoints = RECESSION_CYCLES.length;

        for (let ctr = 0; ctr < totalPoints; ctr += 1) {
            const recessionPeriodStart = RECESSION_CYCLES[ctr][0];
            let recessionPeriodEnd = RECESSION_CYCLES[ctr][1];
            // The recession period end is only null if we are currently
            // in the middle of a recession. If that is the case we set
            // the recession end date to be today since we do not know when
            // it will end.
            if (recessionPeriodEnd === null) {
                recessionPeriodEnd = new Date().getTime();
            }
            plotBands.push({
                from: recessionPeriodStart,
                to: recessionPeriodEnd,
                color: config.plotBandsColor,
            });
        }

        this.highchartOptions.xAxis.plotBands = plotBands;
    }

    setAreaChartThreshold(): void {
        // If any series is an area chart and has a negative value, set its area threshold to be 0
        for (let i = 0; i < this.highchartOptions.series.length; i += 1) {
            if (this.highchartOptions.series[i].type === 'area') {
                for (let j = 0; j < this.highchartOptions.series[i].data.length; j += 1) {
                    const datapoint = this.highchartOptions.series[i].data[j];
                    if (datapoint[1] < 0) {
                        this.highchartOptions.series[i].threshold = 0;
                        break;
                    }
                }
            }
        }
    }

    getChartDateRange(
        skipFirstTwo: boolean,
        specificDate: Date | null = null
    ): {
        min: number;
        max: number;
    } {
        // A shared x-axis should get the min and max of all chart data sets
        const chartData = this.chartInfo.chart_data;
        let firstIndex = 0;
        const minDates: number[] = [];
        const maxDates: number[] = [];
        const dates = {
            min: 0,
            max: 0,
        };

        if (skipFirstTwo) {
            firstIndex = 2;
        }

        // We don't assume that the data is presented to us chronologically, so we find the min and max datetimes here
        for (let i = 0; i < chartData.length; i += 1) {
            for (let j = 0; j < chartData[i].length; j += 1) {
                // In cases when we skip the first two but the number of data is lower (single day)
                /* eslint-disable prefer-destructuring, no-continue */
                if (chartData[i][j].raw_data.length <= firstIndex) {
                    if (chartData[i][j].raw_data.length > 0) {
                        // Single day but only has one point, set min and max to be that point
                        dates.min = chartData[i][j].raw_data[0][0];
                        dates.max = chartData[i][j].raw_data[0][0];
                    }
                    continue;
                }
                // If we are given a specific date, we want to find the min date on that date
                if (specificDate) {
                    const minDateMatchIndex = chartData[i][j].raw_data.findIndex((date: any) =>
                        isSameDateUTC(new Date(date[0]), specificDate)
                    );

                    if (minDateMatchIndex !== -1) {
                        minDates.push(chartData[i][j].raw_data[minDateMatchIndex][0]);
                    }
                } else {
                    minDates.push(chartData[i][j].raw_data[firstIndex][0]);
                }
                maxDates.push(chartData[i][j].raw_data[chartData[i][j].raw_data.length - 1][0]);
            }
        }

        if (minDates.length !== 0 && maxDates.length !== 0) {
            dates.min = Math.min.apply({}, minDates);
            dates.max = Math.max.apply({}, maxDates);
        }

        return dates;
    }

    setSingleDayIntradayTicks(range: { min: number; max: number }): void {
        // The xAxis max end date will be the later of the max date's time or 4 pm on that date for market close
        const d = new Date(range.max);
        const tickStartEpoch = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 10, 0, 0, 0);
        const tickEndEpoch = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 16, 0, 0, 0);
        const marketInterval = tickEndEpoch - tickStartEpoch;

        this.highchartOptions.xAxis.min = Math.min(range.min, tickStartEpoch);
        this.highchartOptions.xAxis.max = Math.max(range.max, tickEndEpoch);

        // We determine the number of ticks by iterating through valid intervals and finding the
        // first one that is equal or greater than the given xAxis tickAmount
        // INDEPENDENT VARIABLES: xAxis.tickAmount, validIntervals, marketInterval
        const validIntervals = [
            3 * TIME_UNIT_SIZE.hour,
            2 * TIME_UNIT_SIZE.hour,
            TIME_UNIT_SIZE.hour,
            30 * TIME_UNIT_SIZE.minute,
        ];
        let numTicks = 0;
        let tickInterval = 0;

        for (let i = 0; i < validIntervals.length; i += 1) {
            numTicks = 0;
            tickInterval = validIntervals[i];
            // Find tickInterval that satisfies number of ticks minimum
            for (let j = 0; j <= marketInterval; j += tickInterval, numTicks += 1);
            if (numTicks >= this.highchartOptions.xAxis.tickAmount) {
                break;
            }
        }

        // Normally show something like 10am unless interval is half an hour then 10:30am.
        const isHalfHour = tickInterval === 30 * TIME_UNIT_SIZE.minute;
        if (this.locale === Language.FRENCH) {
            this.highchartOptions.xAxis.labels.formatter = function () {
                const date = new Date(this.value as number);
                const hours = date.getUTCHours();
                if (isHalfHour) {
                    const minutes = date.getUTCMinutes();
                    return minutes === 0 ? `${hours} h` : `${hours} h ${String(minutes).padStart(2, '0')}`;
                }
                return `${hours} h`;
            };
        } else {
            this.highchartOptions.xAxis.labels.format = '{value:%l%P}';
            if (isHalfHour) {
                this.highchartOptions.xAxis.labels.format = '{value:%l:%M%P}';
            }
        }

        // We will set the tick interval by starting with the tickStartEpoch and incrementing by the tickInterval
        let tickEpoch = tickStartEpoch;
        this.highchartOptions.xAxis.tickPositions = [];
        for (let i = 0; i < numTicks; i += 1) {
            this.highchartOptions.xAxis.tickPositions.push(tickEpoch);
            tickEpoch += tickInterval;
        }
    }

    setSingleDayIntradayBreaks(): void {
        const { chartData } = this;

        for (let i = 0; i < chartData.length; i += 1) {
            for (let j = 0; j < chartData[i].length; j += 1) {
                const dataSeries = chartData[i][j];
                if (dataSeries.raw_data.length > 2) {
                    this.highchartOptions.xAxis.breaks = [
                        {
                            from: dataSeries.raw_data[1][0],
                            to: dataSeries.raw_data[2][0],
                        },
                    ];
                }
            }
        }
    }

    isSingleDayIntradayChart(): boolean {
        if (!this.hasIntradayData()) {
            return false;
        }

        const range = this.getChartDateRange(true);

        if (range.min === null || range.max === null) {
            return false;
        }
        if (range.max - range.min > TIME_UNIT_SIZE.day) {
            return false;
        }

        return true;
    }

    isMultiDayIntradayChart(): boolean {
        if (!this.hasIntradayData()) {
            return false;
        }

        const range = this.getChartDateRange(true);
        if (range.min === null || range.max === null) {
            return false;
        }
        if (range.max - range.min <= TIME_UNIT_SIZE.day) {
            return false;
        }

        return true;
    }

    isMultiYearChart(): boolean {
        const range = this.getChartDateRange(false);

        if (range.min === null || range.max === null) {
            return false;
        }
        if (range.max - range.min < TIME_UNIT_SIZE.year * MULTI_YEAR_THRESHOLD) {
            return false;
        }

        return true;
    }

    isMultiMonthChart(): boolean {
        const range = this.getChartDateRange(false);

        if (range.min === null || range.max === null) {
            return false;
        }
        if (
            range.max - range.min < TIME_UNIT_SIZE.month * MULTI_MONTH_THRESHOLD ||
            range.max - range.min > TIME_UNIT_SIZE.year
        ) {
            return false;
        }

        return true;
    }

    hasIntradayData(): boolean {
        const { chartData } = this;

        let hasIntradayData = false;
        for (let i = 0; i < chartData.length; i += 1) {
            for (let j = 0; j < chartData[i].length; j += 1) {
                if (chartData[i][j].frequency === 'intraday') {
                    hasIntradayData = true;
                    break;
                }
            }
        }

        return hasIntradayData;
    }

    hasOnlySingleFrequencyData(frequency: string): boolean {
        const { chartData } = this;

        for (let i = 0; i < chartData.length; i += 1) {
            for (let j = 0; j < chartData[i].length; j += 1) {
                if (chartData[i][j].frequency !== frequency) {
                    return false;
                }
            }
        }
        return true;
    }

    setQuarterlyDateTicks(): void {
        const { chartData } = this;
        const quarterlyTicks = [] as any;

        // Logic from old ychart_fund for calculating quarterly ticks
        const uniqueTimeMap = {} as any;
        // Get uniqueTimeMap across all charts, this could just be an ES6 Set
        for (let i = 0; i < chartData.length; i += 1) {
            for (let j = 0; j < chartData[i].length; j += 1) {
                for (let k = 0; k < chartData[i][j].raw_data.length; k += 1) {
                    uniqueTimeMap[chartData[i][j].raw_data[k][0]] = 1;
                }
            }
        }

        const uniqueTimeSequence = [] as any[];
        // obj iteration
        Object.keys(uniqueTimeMap).forEach((uniqueTime) => {
            uniqueTimeSequence.push(parseInt(uniqueTime, 10));
        });

        const sortFunc = (a: number, b: number): number => a - b;
        uniqueTimeSequence.sort(sortFunc);

        let tickInterval;
        let tickEnd;
        if (uniqueTimeSequence.length <= this.highchartOptions.xAxis.tickAmount) {
            tickInterval = 1;
            tickEnd = uniqueTimeSequence.length - 1;
        } else {
            tickInterval = Math.ceil(uniqueTimeSequence.length / this.highchartOptions.xAxis.tickAmount);
            tickEnd = Math.ceil(uniqueTimeSequence.length - tickInterval / 3);
            tickInterval = Math.ceil(tickEnd / this.highchartOptions.xAxis.tickAmount);
        }

        let numItems = 1;
        let itemCount = 1;
        for (let i = 0; i < uniqueTimeSequence.length; i += 1, itemCount += 1) {
            if (itemCount > tickEnd) {
                break;
            }
            // If this is the first tick, no label
            if (itemCount === 1) {
                numItems = 1;
            } else if (numItems === tickInterval) {
                numItems = 1;
                quarterlyTicks.push(uniqueTimeSequence[i]);
            } else {
                numItems += 1;
            }
        }

        /* eslint-disable no-param-reassign */
        const quarterPrefix = this.locale === Language.FRENCH ? 'T' : 'Q';
        const Q = (timestamp: number): string => {
            const date = new Date(timestamp);
            const month = date.getUTCMonth() + 1;

            if (month === 1) {
                timestamp -= TIME_UNIT_SIZE.year;
            }

            switch (month) {
                case 2:
                case 3:
                case 4:
                    return `${quarterPrefix}1`;
                case 5:
                case 6:
                case 7:
                    return `${quarterPrefix}2`;
                case 8:
                case 9:
                case 10:
                    return `${quarterPrefix}3`;
                case 11:
                case 12:
                case 1:
                    return `${quarterPrefix}4`;
                default:
                    return '';
            }
        };

        Highcharts.dateFormats = {
            Q,
        };
        this.highchartOptions.tooltip.xDateFormat = "%Q '%y";
        this.highchartOptions.xAxis.labels.formatter = function () {
            return `${Q(this.value)} '${Highcharts.dateFormat('%y', this.value)}`;
        };
        this.highchartOptions.plotOptions.line.states = {
            hover: {
                enabled: false,
            },
        };

        this.highchartOptions.xAxis.tickPositions = quarterlyTicks;
    }

    setMultiDayIntradayTicks(range: { min: number; max: number }): void {
        this.highchartOptions.xAxis.min = range.min;
        this.highchartOptions.xAxis.max = range.max;
        this.highchartOptions.xAxis.minTickInterval = TIME_UNIT_SIZE.day;
        const multiDayLocale = this.locale;
        this.highchartOptions.xAxis.labels.formatter = function () {
            const date = new Date(this.value as number);
            return date.toLocaleDateString(multiDayLocale, { month: 'short', day: 'numeric', timeZone: 'UTC' });
        };
    }

    setMultiDayIntradayBreaks(): void {
        const { chartData } = this;
        // Insert gaps for anywhere with greater than 8 hours
        this.highchartOptions.xAxis.ordinal = true;

        let highchartSeriesIdx = 0;
        for (let i = 0; i < chartData.length; i += 1) {
            for (let j = 0; j < chartData[i].length; j += 1) {
                if (chartData[i][j].frequency === 'intraday') {
                    this.highchartOptions.series[highchartSeriesIdx].gapSize = TIME_UNIT_SIZE.hour * 8;
                    this.highchartOptions.series[highchartSeriesIdx].gapUnit = 'value';
                }
                highchartSeriesIdx += 1;
            }
        }
    }

    /* Methods that get processed versions of options & config */

    /**
     * Returns width of y-axis.
     * Note: In situations where y-axis width is null, this means that we want
     * Highcharts to figure out the y-axis width as needed. If the chart is set up correctly
     * codepaths that depend on a y-axis width should never be reached. Like, you should never
     * have a chart that has annotations but no y-axis width, and you should never have a chart
     * that has several aligned sub-charts and no y-axis width, but in case the chart is set up
     * incorrectly then return a 0 for y-axis width so that the chart doesn't completely blow up.
     *
     * @returns {number} Width of y-axis
     * @memberof BaseYchartFund
     */
    getYAxisWidth(): number {
        if (this.config.yAxisWidth === null) {
            return 0;
        }
        return this.config.yAxisWidth as number;
    }

    getXAxisHeight(): number {
        const xAxisLabelsY = (((this.highchartOptions || 0).xAxis || 0).labels || 0).y || 0;
        return xAxisLabelsY + 3;
    }
}
